import React, { useState, useEffect } from 'react';
import { 
  HiHashtag,
  HiOfficeBuilding,
  HiDocumentText,
  HiPhone,
  HiMail,
  HiLocationMarker,
  HiCurrencyDollar,
  HiCheck,
  HiExclamation,
  HiRefresh,
  HiSaveAs,
} from 'react-icons/hi';
// Add social media icons
import { FaInstagram, FaFacebook } from 'react-icons/fa';
import { usePermissionViewModel } from '../../permissions/viewmodel/permission-viewmodel';

const StoreInformation = ({ storeInfo, onSave, isSaving }) => {
  // Simple form state management
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const {hasPermission} = usePermissionViewModel('paramètres')

  
  // Initialize form with storeInfo or empty values
  useEffect(() => {
    if (storeInfo) {
      setFormValues(storeInfo);
      setErrors({});
      setTouched({});
      setIsDirty(false);
    }
  }, [storeInfo]);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Handle number conversion for delivery fee
    if (name === 'fraisDeLivraison') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }
    
    setFormValues(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    setIsDirty(true);
    
    // Validate on change if field has been touched
    if (touched[name]) {
      validateField(name, processedValue);
    }
  };
  
  // Mark field as touched and validate on blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };
  
  // Field validation
  const validateField = (name, value) => {
    let newErrors = { ...errors };
    
    switch (name) {
      case 'codeSociete':
      case 'societe':
      case 'matriculeFiscal':
      case 'adresse':
      case 'tel':
        if (!value?.trim()) {
          newErrors[name] = `Ce champ est requis`;
        } else {
          delete newErrors[name];
        }
        break;
        
      case 'email':
        if (!value?.trim()) {
          newErrors[name] = 'Email est requis';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          newErrors[name] = 'Format d\'email invalide';
        } else {
          delete newErrors[name];
        }
        break;
        
      case 'fraisDeLivraison':
        if (value < 0) {
          newErrors[name] = 'Doit être positif';
        } else {
          delete newErrors[name];
        }
        break;
        
      // Fixed: Use lienInstagram instead of instagramLink
      case 'lienInstagram':
        if (value && !value.includes('instagram.com')) {
          newErrors[name] = 'Lien Instagram invalide';
        } else {
          delete newErrors[name];
        }
        break;
        
      // Fixed: Use lienFacebook instead of facebookLink
      case 'lienFacebook':
        if (value && !value.includes('facebook.com')) {
          newErrors[name] = 'Lien Facebook invalide';
        } else {
          delete newErrors[name];
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['codeSociete', 'societe', 'matriculeFiscal', 'adresse', 'tel', 'email'];
    
    // Check required fields
    requiredFields.forEach(field => {
      if (!formValues[field]?.toString().trim()) {
        newErrors[field] = `Ce champ est requis`;
      }
    });
    
    // Email validation
    if (formValues.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formValues.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    // Delivery fee validation
    if (formValues.fraisDeLivraison < 0) {
      newErrors.fraisDeLivraison = 'Doit être positif';
    }
    
    // Fixed: Social media validation with correct field names
    if (formValues.lienInstagram && !formValues.lienInstagram.includes('instagram.com')) {
      newErrors.lienInstagram = 'Lien Instagram invalide';
    }
    
    if (formValues.lienFacebook && !formValues.lienFacebook.includes('facebook.com')) {
      newErrors.lienFacebook = 'Lien Facebook invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(formValues).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Validate all fields
    if (validateForm()) {
      onSave(formValues);
    }
  };
  
  // Reset form to initial state
  const handleReset = () => {
    setFormValues(storeInfo || {});
    setErrors({});
    setTouched({});
    setIsDirty(false);
  };
  
  // Form is valid if there are no errors
  const isFormValid = Object.keys(errors).length === 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-[#174540] to-[#3d7d79] px-6 py-4">
        <div className="flex items-center">
          <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
            <HiOfficeBuilding className="h-6 w-6 text-white" />
          </div>
          <h2 className="ml-3 text-xl font-bold text-white">Informations de l'entreprise</h2>
        </div>
        <p className="mt-2 text-white/80 text-sm">
          Ces informations seront affichées sur vos factures et documents commerciaux.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code Société
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiHashtag className={`h-5 w-5 ${errors.codeSociete ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
              disabled={!hasPermission('write')}
                type="text"
                name="codeSociete"
                value={formValues.codeSociete || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Exemple: SHOP123"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.codeSociete 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.codeSociete && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.codeSociete ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.codeSociete || 'Identifiant unique de votre entreprise'}
            </p>
          </div>
          
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de Société
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiOfficeBuilding className={`h-5 w-5 ${errors.societe ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
               disabled={!hasPermission('write')}
                type="text"
                name="societe"
                value={formValues.societe || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Votre nom commercial"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.societe 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.societe && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.societe ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.societe || 'Nom commercial qui apparaîtra sur vos documents'}
            </p>
          </div>
          
          {/* Tax ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Matricule Fiscal
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiDocumentText className={`h-5 w-5 ${errors.matriculeFiscal ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
               disabled={!hasPermission('write')}
                type="text"
                name="matriculeFiscal"
                value={formValues.matriculeFiscal || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Exemple: 1234567/A/M/000"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.matriculeFiscal 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.matriculeFiscal && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.matriculeFiscal ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.matriculeFiscal || 'Numéro d\'identification fiscale'}
            </p>
          </div>
          
          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiPhone className={`h-5 w-5 ${errors.tel ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
               disabled={!hasPermission('write')}
                type="text"
                name="tel"
                value={formValues.tel || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="+216 XX XXX XXX"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.tel 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.tel && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.tel ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.tel || 'Numéro de contact principal'}
            </p>
          </div>
          
          {/* Email - Full width */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiMail className={`h-5 w-5 ${errors.email ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <input
               disabled={!hasPermission('write')}
                type="email"
                name="email"
                value={formValues.email || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="contact@votreentreprise.com"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.email 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.email && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.email ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.email || 'Adresse email professionnelle'}
            </p>
          </div>
          
          {/* Address - Full width */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                <HiLocationMarker className={`h-5 w-5 ${errors.adresse ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              <textarea
               disabled={!hasPermission('write')}
                name="adresse"
                value={formValues.adresse || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={3}
                placeholder="Adresse complète de l'entreprise"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.adresse 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.adresse && (
                <div className="absolute top-3 right-3 pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.adresse ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.adresse || 'Adresse physique de votre entreprise'}
            </p>
          </div>
          
          {/* Instagram Link - Fixed field name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien Instagram
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaInstagram className={`h-5 w-5 ${errors.lienInstagram ? 'text-red-400' : 'text-pink-500'}`} />
              </div>
              <input
               disabled={!hasPermission('write')}
                type="url"
                name="lienInstagram"
                value={formValues.lienInstagram || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="https://www.instagram.com/votre_compte"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.lienInstagram 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.lienInstagram && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.lienInstagram ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.lienInstagram || 'Lien vers votre page Instagram (optionnel)'}
            </p>
          </div>
          
          {/* Facebook Link - Fixed field name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien Facebook
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFacebook className={`h-5 w-5 ${errors.lienFacebook ? 'text-red-400' : 'text-blue-600'}`} />
              </div>
              <input
               disabled={!hasPermission('write')}
                type="url"
                name="lienFacebook"
                value={formValues.lienFacebook || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="https://www.facebook.com/votre_page"
                className={`
                  block w-full pl-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.lienFacebook 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              {errors.lienFacebook && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiExclamation className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            <p className={`mt-1 text-xs ${errors.lienFacebook ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.lienFacebook || 'Lien vers votre page Facebook (optionnel)'}
            </p>
          </div>
          
          {/* Delivery Fee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Frais de Livraison
            </label>
            <div className="relative mt-1 rounded-md">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <span className={`inline-block font-medium text-sm ${errors.fraisDeLivraison ? 'text-red-500' : 'text-gray-500'}`}>TND</span>
              </div>
              <input
               disabled={!hasPermission('write')}
                type="number"
                name="fraisDeLivraison"
                value={formValues.fraisDeLivraison !== undefined ? formValues.fraisDeLivraison : ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={`
                  block w-full pl-16 pr-10 py-3 border-0 text-gray-900 ring-1 ring-inset 
                  rounded-lg focus:ring-2 focus:ring-inset sm:text-sm
                  ${errors.fraisDeLivraison 
                    ? 'ring-red-300 focus:ring-red-500 placeholder-red-300' 
                    : 'ring-gray-300 focus:ring-[#174540] placeholder-gray-400'}
                `}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                {errors.fraisDeLivraison ? (
                  <HiExclamation className="h-5 w-5 text-red-500" />
                ) : (
                  <HiCurrencyDollar className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            <p className={`mt-1 text-xs ${errors.fraisDeLivraison ? 'text-red-500' : 'text-gray-500'}`}>
              {errors.fraisDeLivraison || 'Frais standard appliqués aux commandes'}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleReset}
            disabled={isSaving || !isDirty}
            className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-medium 
              border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]
              disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <HiRefresh className="mr-2 h-4 w-4" />
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSaving || (!isFormValid && Object.keys(touched).length > 0) || !isDirty|| !hasPermission('write')}
            className="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-medium
              bg-gradient-to-r from-[#174540] to-[#3d7d79] text-white shadow-sm
              hover:from-[#123935] hover:to-[#2c6561]
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]
              disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <HiSaveAs className="mr-2 h-4 w-4" />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoreInformation;